import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import { HappyUsers } from '@/components/landing/HappyUsers';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

const MEDIA = 'https://media.easy-peasy.ai/landing/video';

type VideoModelChip = {
  name: string;
  viewBox?: string;
  path?: string;
};

// Video-only model set (chat/image brands like Runway or Sora deliberately
// excluded — they are not in videoModels.ts).
const VIDEO_MODELS: VideoModelChip[] = [
  {
    name: 'Seedance 2.0',
    viewBox: '0 0 32 32',
    path: 'M5.915 25.44 1 26.702V4.311l4.915 1.263zm23.75 1.31-4.924 1.264V3l4.924 1.254zm-15.952-.627-4.915 1.264v-13.19l4.915 1.263zm3.21-13.883 4.925-1.264v13.19l-4.924-1.264z',
  },
  { name: 'Veo 3.1' },
  {
    name: 'Kling 3.0',
    viewBox: '0 0 24 24',
    path: 'M21.973 15.22a287 287 0 0 1-2.218-1.758c-.4-.32-.825-.659-1.347-1.073 3.47-4.107 1.171-7.015-.336-8.523-1.883-1.884-4.869-2.44-7.824-1.922-3.49.613-6.526 2.806-8.331 6.02l-.317.562s3.7 2.937 4.357 3.455c-3.27 4.432-1.227 7.086-.223 8.091 1.477 1.477 3.952 2.296 6.207 2.296.62 0 1.256-.054 1.894-.166 3.49-.613 6.527-2.807 8.333-6.02l.317-.563-.507-.398zM5.264 9.508a317 317 0 0 0-1.706-1.355c1.608-2.515 4.11-4.224 6.948-4.722 1.782-.312 3.51-.112 4.96.548-3.776.91-7.148 3.758-8.802 6.645L5.262 9.507zm4.096-.265c2.787-3.17 6.368-4.578 7.995-3.146s.69 5.163-2.099 8.333c-2.787 3.17-6.368 4.577-7.995 3.146-1.627-1.433-.69-5.164 2.099-8.333m4.213 11.476c-1.783.313-3.509.113-4.96-.547 3.776-.91 7.149-3.758 8.801-6.644.548.435.988.785 1.402 1.116.534.427 1.046.835 1.704 1.355-1.608 2.516-4.109 4.225-6.947 4.722z',
  },
  { name: 'MiniMax H3' },
  { name: 'Grok Imagine' },
  { name: 'Gemini Omni Flash 1.1' },
  { name: 'WAN 3.0' },
  { name: 'LTX-2' },
  { name: 'Pixverse v5.5' },
  { name: 'Kling Motion Control' },
];

export const VideoModelChipsMarquee = () => (
  <div className='relative w-full overflow-hidden'>
    <div className='flex w-max items-center gap-x-8 animate-scroll-logos motion-reduce:animate-none'>
      {[false, true].map((isDuplicate) => (
        <React.Fragment key={isDuplicate ? 'dup' : 'main'}>
          {VIDEO_MODELS.map((model) => (
            <div
              key={`${isDuplicate ? 'dup-' : ''}${model.name}`}
              className='flex shrink-0 items-center gap-2 pr-8'
              aria-hidden={isDuplicate || undefined}
            >
              {model.path && (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox={model.viewBox}
                  width={16}
                  height={16}
                  aria-hidden='true'
                  className='h-[16px] w-auto shrink-0 fill-white/50'
                >
                  <path d={model.path} />
                </svg>
              )}
              <span className='shrink-0 text-base font-normal text-white/50'>
                {model.name}
              </span>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
    <div className='pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#050509] to-transparent' />
    <div className='pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#050509] to-transparent' />
  </div>
);

const VideoHero = () => {
  const { t } = useLocale('ai-videos');

  return (
    <section className='relative overflow-hidden bg-[#050509]'>
      {/* Full-bleed background film */}
      <video
        className='absolute inset-0 h-full w-full object-cover'
        src={`${MEDIA}/hero2.mp4`}
        poster={`${MEDIA}/hero2-poster.jpg`}
        autoPlay
        muted
        loop
        playsInline
        preload='auto'
      />

      {/* Legibility overlays */}
      <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_65%_60%_at_50%_45%,rgba(5,5,9,0.72),rgba(5,5,9,0.35)_65%,rgba(5,5,9,0.15))]' />
      <div className='pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#050509]/90 to-transparent' />
      <div className='pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[#050509] to-transparent' />

      {/* Navigation */}
      <div className='absolute top-0 left-0 w-full z-30'>
        <LandingPageNavigation variant='light' forceDark />
      </div>

      {/* Content */}
      <div className='relative z-20 mx-auto flex min-h-[92svh] max-w-4xl flex-col items-center justify-center px-4 pt-32 pb-28 text-center sm:px-6'>
        <Link
          href='/ai-video-generator/seedance-2-5'
          className='mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-200 backdrop-blur-md transition-colors hover:border-white/35 sm:text-sm'
        >
          <span className='rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] font-bold text-white'>
            {t('NEW')}
          </span>
          {t('Unrestricted Seedance 2.5 — 30-second takes')}
          <span aria-hidden='true'>→</span>
        </Link>

        <h1 className='text-5xl font-extrabold tracking-tight text-white drop-shadow-2xl sm:text-6xl md:text-7xl lg:text-[5rem]'>
          {t('Direct your story.')}
          <span className='mt-1 block bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text pb-2 text-transparent'>
            {t('AI makes the film.')}
          </span>
        </h1>

        <p className='mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-slate-200 drop-shadow-lg md:text-xl'>
          {t(
            'Turn text or images into cinematic video with sound — powered by Seedance 2.5, Veo 3.1, Kling and every other top model. Up to 30 seconds, up to 4K.',
          )}
        </p>

        <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
          <Link
            href='/ai-videos'
            className='group inline-flex h-[58px] min-w-[230px] items-center justify-center gap-2 rounded-full bg-indigo-500 px-8 text-lg font-bold text-white shadow-[0_8px_40px_-8px_rgba(99,102,241,0.7)] transition-all hover:bg-indigo-400 hover:shadow-[0_8px_50px_-6px_rgba(99,102,241,0.9)]'
          >
            {t('Start creating free')}
            <span
              aria-hidden='true'
              className='transition-transform group-hover:translate-x-1'
            >
              →
            </span>
          </Link>
          <a
            href='#showcase'
            className='inline-flex h-[58px] min-w-[180px] items-center justify-center rounded-full border border-white/25 bg-black/25 px-8 text-lg font-semibold text-white backdrop-blur-md transition-all hover:border-white/45 hover:bg-black/40'
          >
            {t('Watch examples')}
          </a>
        </div>

        <div className='mt-8 flex justify-center'>
          <HappyUsers fromLandingPage={true} className='items-center' />
        </div>
      </div>

      {/* Video model chips pinned to hero bottom */}
      <div className='relative z-20 pb-6'>
        <VideoModelChipsMarquee />
      </div>
    </section>
  );
};

export default VideoHero;
